import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, doc, getDoc, setDoc, Timestamp, updateDoc, deleteDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { db, secondaryAuth } from '../components/firebase-config.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarPlus, faSearch, faCalendarAlt, faUserPlus, faSpinner, faCheck,
  faArrowLeft, faCalendarCheck, faInfoCircle, faClock, faClinicMedical,
  faEdit, faTrash, faExclamationTriangle, faUser, faEnvelope, faPhone, faMars, faVenus
} from '@fortawesome/free-solid-svg-icons';
import { Form, Button, Card, Alert, Tabs, Tab, Spinner, Modal, Container, Row, Col, Badge } from 'react-bootstrap';
import axios from 'axios';

const PatientRegistration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Paramètres de l'URL
  const medecinId = searchParams.get('medecinId');
  const structureId = searchParams.get('structureId');
  const medecinNom = searchParams.get('medecinNom');
  const medecinTitre = searchParams.get('medecinTitre') || 'Dr'; 
  const medecinSpecialite = searchParams.get('medecinSpecialite');
  const structureNom = searchParams.get('structureNom');
  const [rdvCounter, setRdvCounter] = useState(1);
  const actionParam = searchParams.get('action') || 'rdv'; // inscription, rdv, both
  const typeConsultationParam = searchParams.get('typeConsultation'); // cabinet, domicile, mixte

  // États
  const [activeTab, setActiveTab] = useState('rdv'); // 'rdv' ou 'verification'
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [step, setStep] = useState('initial'); // 'initial', 'checkPatient', 'register', 'appointment', 'success', 'edit', 'cancel', 'registrationSuccess'
  
  // Modals
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  // Formulaires
  const [phoneForm, setPhoneForm] = useState({ telephone: '' });
  const [verificationForm, setVerificationForm] = useState({ code: '' });
  const [patientForm, setPatientForm] = useState({
    nom: '',
    dateNaissance: '',
    sexe: 'non-specifie',
    email: '',
    telephone: '',
    password: '',
    confirmPassword: ''
  });
  const [rdvForm, setRdvForm] = useState({
    date: new Date().toISOString().split('T')[0],
    heure: '08:00',
    duree: 30,
    motif: '',
    typeConsultation: typeConsultationParam === 'domicile' ? 'domicile' : 'presentiel'
  });
  
  // Données
  const [patientFound, setPatientFound] = useState(null);
  const [rdvFound, setRdvFound] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  
  // Configuration pour l'API WhatsApp
  const MAYTAPI_CONFIG = {
    PRODUCT_ID: "5e994f76-4f49-4057-aca4-6bef3b1de795",
    API_TOKEN: "a0b5ba45-b4da-429e-ba5b-127851d9b92e",
    PHONE_ID: "77695",
  };
  
  // Vérifier les paramètres requis
  useEffect(() => {
    if (!medecinId || !structureId) {
      setMessage({ type: 'danger', text: 'Paramètres manquants. Veuillez scanner à nouveau le QR code.' });
    }
    if (actionParam === 'inscription') {
      setStep('register');
    }
  }, [medecinId, structureId]);
  
  // Charger les créneaux disponibles pour la date sélectionnée
  useEffect(() => {
    const fetchAvailableSlots = async () => {
      if (!medecinId || !structureId || !rdvForm.date) return;
      
      try {
        setLoading(true);
        
        // Récupérer les rendez-vous existants pour cette date
        const rdvQuery = query(
          collection(db, "rendezvous"),
          where("medecinId", "==", medecinId),
          where("structureId", "==", structureId),
          where("date", "==", rdvForm.date),
          where("statut", "!=", "annulé")
        );
        
        const querySnapshot = await getDocs(rdvQuery);
        const existingAppointments = [];
        querySnapshot.forEach((doc) => {
          // Exclure le rendez-vous en cours de modification (s'il existe)
          if (!rdvFound || doc.id !== rdvFound.id) {
            existingAppointments.push(doc.data());
          }
        });
        
        // Générer les créneaux disponibles (de 8h à 18h, par tranches de 30min)
        const slots = [];
        const startHour = 8;
        const endHour = 18;
        
        for (let hour = startHour; hour < endHour; hour++) {
          for (let minute = 0; minute < 60; minute += 30) {
            const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            
            // Vérifier si ce créneau est déjà pris
            const isBooked = existingAppointments.some(rdv => rdv.heure === timeStr);
            
            if (!isBooked) {
              slots.push(timeStr);
            }
          }
        }
        
        setAvailableSlots(slots);
      } catch (error) {
        console.error("Erreur lors du chargement des créneaux disponibles:", error);
        setMessage({ type: 'danger', text: `Erreur: ${error.message}` });
      } finally {
        setLoading(false);
      }
    };
    
    fetchAvailableSlots();
  }, [medecinId, structureId, rdvForm.date, rdvFound]);
  
  // Fonction pour vérifier si le patient existe
  const checkPatientExists = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Formater le numéro de téléphone
      let formattedPhone = phoneForm.telephone.replace(/[\s\-()]/g, "");
      if (formattedPhone.startsWith("0")) {
        formattedPhone = "+221" + formattedPhone.substring(1);
      } else if (!formattedPhone.startsWith("+")) {
        formattedPhone = "+221" + formattedPhone;
      }
      
      // Rechercher le patient par numéro de téléphone
      const patientsQuery = query(
        collection(db, "patients"),
        where("telephone", "==", formattedPhone)
      );
      
      const querySnapshot = await getDocs(patientsQuery);
      
      if (!querySnapshot.empty) {
        // Patient trouvé
        const patientData = querySnapshot.docs[0].data();
        const patientId = querySnapshot.docs[0].id;
        setPatientFound({
          id: patientId,
          ...patientData
        });
        
        // Vérifier si le patient est déjà affilié à cette structure
        const affiliationsQuery = query(
          collection(db, "affiliations"),
          where("patientId", "==", patientId),
          where("structureId", "==", structureId),
          where("statut", "==", "acceptée")
        );
        
        const affiliationsSnapshot = await getDocs(affiliationsQuery);
        
        // Si le patient n'est pas encore affilié à cette structure, créer l'affiliation
        if (affiliationsSnapshot.empty) {
          await addDoc(collection(db, 'affiliations'), {
            patientId: patientId,
            patientNom: patientData.nom,
            structureId: structureId,
            structureNom: structureNom || '',
            medecinId: medecinId, // Médecin référent pour cette structure
            statut: 'acceptée', // Affiliation automatique via QR code médecin
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            source: 'qr_code_medecin' // Traçabilité de la source d'affiliation
          });
        }
        
        // Si le patient est trouvé et que nous sommes dans l'onglet RDV, passer à l'étape de prise de RDV
        if (activeTab === 'rdv') {
          setStep('appointment');
        } else {
          setMessage({ type: 'warning', text: 'Aucun patient trouvé avec ce numéro de téléphone.' });
        }
      } else {
        // Patient non trouvé
        setPatientFound(null);
        
        // Si nous sommes dans l'onglet RDV
        if (activeTab === 'rdv') {
          if (actionParam === 'rdv') {
            // QR code dédié à la prise de RDV : on ne propose pas l'inscription
            setMessage({
              type: 'warning',
              text: "Nous n'avons trouvé aucun patient associé à ce numéro. Veuillez d'abord créer votre compte via un QR code d'inscription."
            });
          } else {
            // Scénarios inscription ou both : on propose l'inscription
            setPatientForm({
              ...patientForm,
              telephone: formattedPhone
            });
            setStep('register');
          }
        } else {
          setMessage({ type: 'warning', text: 'Aucun patient trouvé avec ce numéro de téléphone.' });
        }
      }
      
    } catch (error) {
      console.error("Erreur lors de la vérification du patient:", error);
      setMessage({ type: 'danger', text: `Erreur: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour vérifier un rendez-vous
  const verifyAppointment = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Rechercher le rendez-vous par code de vérification
      const rdvQuery = query(
        collection(db, "rendezvous"),
        where("verificationCode", "==", verificationForm.code)
      );
      
      const querySnapshot = await getDocs(rdvQuery);
      
      if (!querySnapshot.empty) {
        // Rendez-vous trouvé
        const rdvData = querySnapshot.docs[0].data();
        setRdvFound({
          id: querySnapshot.docs[0].id,
          ...rdvData
        });
        
        // Préremplir le formulaire de RDV avec les données existantes
        setRdvForm({
          date: rdvData.date,
          heure: rdvData.heure,
          duree: rdvData.duree,
          motif: rdvData.motif || '',
          typeConsultation: rdvData.typeConsultation
        });
        
        // Récupérer les informations du patient
        const patientDoc = await getDoc(doc(db, "patients", rdvData.patientId));
        if (patientDoc.exists()) {
          setPatientFound({
            id: rdvData.patientId,
            ...patientDoc.data()
          });
        }
        
        setStep('success');
      } else {
        // Rendez-vous non trouvé
        setRdvFound(null);
        setMessage({ type: 'warning', text: 'Aucun rendez-vous trouvé avec ce code de vérification.' });
      }
      
    } catch (error) {
      console.error("Erreur lors de la vérification du rendez-vous:", error);
      setMessage({ type: 'danger', text: `Erreur: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour enregistrer un nouveau patient
  const registerPatient = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Vérifier que les mots de passe correspondent
      if (patientForm.password !== patientForm.confirmPassword) {
        setMessage({ type: 'danger', text: 'Les mots de passe ne correspondent pas.' });
        return;
      }
      
      // Créer un compte utilisateur pour le patient
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        patientForm.email,
        patientForm.password
      );
      
      const patientId = userCredential.user.uid;
      
      // Enregistrer les données du patient dans Firestore
      await setDoc(doc(db, "patients", patientId), {
        nom: patientForm.nom,
        dateNaissance: patientForm.dateNaissance,
        sexe: patientForm.sexe,
        email: patientForm.email,
        telephone: patientForm.telephone,
        medecinId: medecinId, // Associer au médecin du QR code
        structureId: structureId, // Associer à la structure spécifique du QR code
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      // Créer une affiliation spécifique patient-structure pour cette structure
      await addDoc(collection(db, 'affiliations'), {
        patientId: patientId,
        patientNom: patientForm.nom,
        structureId: structureId,
        structureNom: structureNom || '',
        medecinId: medecinId, // Médecin référent pour cette structure
        statut: 'acceptée', // Affiliation automatique via QR code médecin
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        source: 'qr_code_medecin' // Traçabilité de la source d'affiliation
      });
      
      // Créer le message avec les informations de connexion
      const welcomeMessage = 
        `Bonjour ${patientForm.nom},\n\n` +
        `Votre compte patient a été créé avec succès !\n\n` +
        `Voici vos informations de connexion :\n` +
        `Email : ${patientForm.email}\n` +
        `Mot de passe : ${patientForm.password}\n\n` +
        `Vous pouvez vous connecter à votre espace patient à tout moment pour gérer vos rendez-vous.\n\n` +
        `https://www.samdigitale.com/Auth\n\n`+
        `Nous vous recommandons de conserver ces informations en lieu sûr et de changer votre mot de passe lors de votre première connexion.\n\n` +
        `Merci de votre confiance.`;

      // Envoyer le message WhatsApp avec les informations de connexion
      await sendWhatsAppMessage(patientForm.telephone, welcomeMessage);
      
      // Mettre à jour l'état
      setPatientFound({
        id: patientId,
        nom: patientForm.nom,
        email: patientForm.email,
        telephone: patientForm.telephone
      });
      
      // Adapter la suite du flux selon le paramètre d'action du QR code
      if (actionParam === 'inscription') {
        // Pour un QR code dédié uniquement à l'inscription, on affiche un écran de succès dédié
        setStep('registrationSuccess');
      } else {
        // Comportement actuel : proposer la prise de rendez-vous
        setMessage({ type: 'success', text: 'Votre compte a été créé avec succès. Sélectionnez maintenant la date et l\'heure de votre rendez-vous.' });
        setStep('appointment');
      }
      
    } catch (error) {
      console.error("Erreur lors de l'inscription:", error);
      setMessage({ type: 'danger', text: `Erreur: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour prendre un rendez-vous
  const bookAppointment = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Vérifier que tous les champs requis sont remplis
      if (!rdvForm.date || !rdvForm.heure || !patientFound) {
        setMessage({ type: 'danger', text: 'Veuillez remplir tous les champs obligatoires' });
        return;
      }
      
      // Récupérer les informations du médecin
      const medecinDoc = await getDoc(doc(db, "medecins", medecinId));
      const medecinData = medecinDoc.exists() ? medecinDoc.data() : null;
      
      // Récupérer les informations de la structure
      const structureDoc = await getDoc(doc(db, "structures", structureId));
      const structureData = structureDoc.exists() ? structureDoc.data() : null;
      
      // Générer un code de vérification pour le rendez-vous
      const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
      
      // Récupérer le dernier index de rendez-vous pour ce médecin
      const rdvIndexQuery = query(
        collection(db, "rendezvous"),
        where("medecinId", "==", medecinId),
        where("structureId", "==", structureId)
      );
      
      const rdvIndexSnapshot = await getDocs(rdvIndexQuery);
      const currentIndex = rdvIndexSnapshot.size + 1;
      
      // Créer l'objet rendez-vous avec l'index
      const rdvData = {
        index: currentIndex,
        patientId: patientFound.id,
        patientNom: ` ${patientFound.nom}`,
        patientEmail: patientFound.email,
        patientTelephone: patientFound.telephone,
        medecinId: medecinId,
        medecinNom: medecinData ? `${medecinData.titre || "Dr"} ${medecinData.nom}` : medecinNom || 'Dr.',
        medecinSpecialite: medecinData?.specialite || medecinSpecialite || '',
        structureId: structureId,
        structureNom: structureData?.nom || structureNom || '',
        date: rdvForm.date,
        heure: rdvForm.heure,
        duree: parseInt(rdvForm.duree),
        motif: rdvForm.motif,
        typeConsultation: rdvForm.typeConsultation,
        statut: 'confirmé',
        verificationCode: verificationCode,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      // Ajouter le rendez-vous à Firestore
      const docRef = await addDoc(collection(db, "rendezvous"), rdvData);
      
      // Envoyer une notification au patient
      const formattedDate = new Date(rdvForm.date).toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      
      const notificationMessage = 
        `Bonjour ${patientFound.nom},\n\n` +
        `Votre ${rdvForm.typeConsultation === "teleconsultation" ? "téléconsultation" : "rendez-vous"} ` +
        `a été confirmé pour le ${formattedDate} à ${rdvForm.heure} avec ${rdvData.medecinNom}.\n\n` +
        `Numéro d'ordre de rendez-vous: ${currentIndex}\n` +
        `Motif: ${rdvForm.motif || "Consultation"}\n` +
        `Lieu: ${rdvData.structureNom}\n\n` +
        `Code de vérification: ${verificationCode}\n\n` +
        `Vous pouvez vérifier l'état de votre rendez-vous en utilisant ce code.\n\n` +
                `https://www.samdigitale.com/Quick\n\n`+
        `Merci de votre confiance.`;
      
      // Envoyer le message WhatsApp
      await sendWhatsAppMessage(patientFound.telephone, notificationMessage);
      
      // Mettre à jour l'état
      setRdvFound({
        id: docRef.id,
        ...rdvData
      });
      
      setMessage({ type: 'success', text: 'Rendez-vous pris avec succès! Un message de confirmation a été envoyé par WhatsApp.' });
      setStep('success');
      
    } catch (error) {
      console.error("Erreur lors de la prise de rendez-vous:", error);
      setMessage({ type: 'danger', text: `Erreur: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour modifier un rendez-vous
  const updateAppointment = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Vérifier que tous les champs requis sont remplis
      if (!rdvForm.date || !rdvForm.heure || !rdvFound) {
        setMessage({ type: 'danger', text: 'Veuillez remplir tous les champs obligatoires' });
        return;
      }
      
      // Mettre à jour les données du rendez-vous
      const updatedData = {
        date: rdvForm.date,
        heure: rdvForm.heure,
        motif: rdvForm.motif,
        typeConsultation: rdvForm.typeConsultation,
        updatedAt: Timestamp.now()
      };
      
      // Mettre à jour le rendez-vous dans Firestore
      await updateDoc(doc(db, "rendezvous", rdvFound.id), updatedData);
      
      // Envoyer une notification au patient
      const formattedDate = new Date(rdvForm.date).toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      
      const notificationMessage = 
        `Bonjour ${patientFound.nom},\n\n` +
        `Votre ${rdvForm.typeConsultation === "teleconsultation" ? "téléconsultation" : "rendez-vous"} ` +
        `a été modifié avec succès.\n\n` +
        `Nouvelles informations:\n` +
        `Date: ${formattedDate}\n` +
        `Heure: ${rdvForm.heure}\n` +
        `Numéro de rendez-vous: ${rdvFound.index}\n` +
        `Motif: ${rdvForm.motif || "Consultation"}\n` +
        `Lieu: ${rdvFound.structureNom}\n\n` +
        `Code de vérification: ${rdvFound.verificationCode}\n\n` +
        `Merci de votre confiance.`;
      
      // Envoyer le message WhatsApp
      await sendWhatsAppMessage(patientFound.telephone, notificationMessage);
      
      // Mettre à jour l'état
      setRdvFound({
        ...rdvFound,
        ...updatedData
      });
      
      setMessage({ type: 'success', text: 'Rendez-vous modifié avec succès! Un message de confirmation a été envoyé par WhatsApp.' });
      setStep('success');
      
    } catch (error) {
      console.error("Erreur lors de la modification du rendez-vous:", error);
      setMessage({ type: 'danger', text: `Erreur: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour annuler un rendez-vous
  const cancelAppointment = async () => {
    try {
      setLoading(true);
      
      // Mettre à jour le statut du rendez-vous
      await updateDoc(doc(db, "rendezvous", rdvFound.id), {
        statut: 'annulé',
        updatedAt: Timestamp.now()
      });
      
      // Envoyer une notification au patient
      const notificationMessage = 
        `Bonjour ${patientFound.nom},\n\n` +
        `Votre rendez-vous n°${rdvFound.index} avec ${rdvFound.medecinNom} prévu le ` +
        `${new Date(rdvFound.date).toLocaleDateString("fr-FR", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        })} à ${rdvFound.heure} a été annulé.\n\n` +
        `Si vous souhaitez reprendre rendez-vous, veuillez utiliser le portail patient.\n\n` +
        `Nous vous remercions de votre compréhension.`;
      
      // Envoyer le message WhatsApp
      await sendWhatsAppMessage(patientFound.telephone, notificationMessage);
      
      // Mettre à jour l'état
      setRdvFound({
        ...rdvFound,
        statut: 'annulé'
      });
      
      setMessage({ type: 'success', text: 'Rendez-vous annulé avec succès! Une notification a été envoyée au patient.' });
      setStep('success');
      setShowCancelModal(false);
      
    } catch (error) {
      console.error("Erreur lors de l'annulation du rendez-vous:", error);
      setMessage({ type: 'danger', text: `Erreur: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour envoyer un message WhatsApp
  const sendWhatsAppMessage = async (phoneNumber, message) => {
    try {
      // Formater le numéro de téléphone pour WhatsApp
      if (!phoneNumber) {
        console.error("Numéro de téléphone manquant!");
        return { success: false, error: "Numéro de téléphone manquant" };
      }

      let formattedNumber = phoneNumber;
      // Retirer tous les espaces, tirets et parenthèses
      formattedNumber = formattedNumber.replace(/[\s\-()]/g, "");

      // S'assurer que le numéro commence par +
      if (!formattedNumber.startsWith("+")) {
        // Si le numéro commence par un 0, le remplacer par le code pays
        if (formattedNumber.startsWith("0")) {
          formattedNumber = "+221" + formattedNumber.substring(1);
        } else {
          // Sinon, simplement ajouter le code pays
          formattedNumber = "+221" + formattedNumber;
        }
      }

      // Vérifier la configuration Maytapi
      if (
        !MAYTAPI_CONFIG.PRODUCT_ID ||
        !MAYTAPI_CONFIG.PHONE_ID ||
        !MAYTAPI_CONFIG.API_TOKEN
      ) {
        console.error("Configuration Maytapi incomplète");
        return { success: false, error: "Configuration Maytapi incomplète" };
      }

      // Envoyer le message via l'API Maytapi
      const response = await axios.post(
        `https://api.maytapi.com/api/${MAYTAPI_CONFIG.PRODUCT_ID}/${MAYTAPI_CONFIG.PHONE_ID}/sendMessage`,
        {
          to_number: formattedNumber,
          type: "text",
          message: message,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-maytapi-key": MAYTAPI_CONFIG.API_TOKEN,
          },
        }
      );

      if (response.status === 200 && response.data.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.message || "Échec de l'envoi du message");
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi du message WhatsApp:", error);
      return { success: false, error: error.message };
    }
  };
  
  // Gérer le changement d'onglet
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setStep('initial');
    setMessage(null);
    setPhoneForm({ telephone: '' });
    setVerificationForm({ code: '' });
    setPatientFound(null);
    setRdvFound(null);
  };
  
  // Gérer les changements de formulaire
  const handlePhoneChange = (e) => {
    setPhoneForm({ ...phoneForm, [e.target.name]: e.target.value });
  };
  
  const handleVerificationChange = (e) => {
    setVerificationForm({ ...verificationForm, [e.target.name]: e.target.value });
  };
  
  const handlePatientChange = (e) => {
    setPatientForm({ ...patientForm, [e.target.name]: e.target.value });
  };
  
  const handleRdvChange = (e) => {
    setRdvForm({ ...rdvForm, [e.target.name]: e.target.value });
  };
  
  // Fonction pour revenir à l'étape précédente
  const goBack = () => {
    if (step === 'register' || step === 'appointment') {
      setStep('initial');
    } else if (step === 'success') {
      setStep('initial');
      setActiveTab('rdv');
    } else if (step === 'registrationSuccess') {
      setStep('initial');
      setActiveTab('rdv');
    } else if (step === 'edit') {
      setStep('success');
    }
  };
  
  // Fonction pour passer à l'édition du rendez-vous
  const goToEdit = () => {
    setStep('edit');
  };
  
  // Vérifier si le rendez-vous peut être modifié (moins de 24h avant)
  const canModifyAppointment = () => {
    if (!rdvFound) return false;
    
    // Vérifier si le rendez-vous est annulé
    if (rdvFound.statut === 'annulé') return false;
    
    // Vérifier si le rendez-vous est dans plus de 24h
    const rdvDateTime = new Date(`${rdvFound.date}T${rdvFound.heure}`);
    const now = new Date();
    const diffHours = (rdvDateTime - now) / (1000 * 60 * 60);
    
    return diffHours > 24;
  };
  
  // Obtenir le jour de la semaine en français
  const getWeekday = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'long' });
  };
  
  return (
    <Container fluid className="patient-portal-container py-4">
      <Row className="justify-content-center">
        <Col lg={10} xl={8}>
          <Card className="shadow-lg border-0 rounded-lg overflow-hidden">
            <Card.Header className="bg-gradient-primary text-white py-3">
              <Container fluid>
                <Row className="align-items-center">
                  <Col>
                    <h4 className="mb-0 fw-bold">
                    {medecinNom ? `${searchParams.get('medecinTitre') || 'Dr'} ${medecinNom}` : 'Portail patient'}
                    {medecinSpecialite && <span className="ms-2 fs-6 fw-normal">({medecinSpecialite})</span>}
                    </h4>
                    {structureNom && (
                      <div className="mt-1 d-flex align-items-center">
                        <FontAwesomeIcon icon={faClinicMedical} className="me-2" />
                        <span className="text-white-50">{structureNom}</span>
                      </div>
                    )}
                  </Col>
                </Row>
              </Container>
            </Card.Header>
            
            <Card.Body className="p-lg-4">
              {/* Message de notification */}
              {message && (
                <Alert 
                  variant={message.type} 
                  onClose={() => setMessage(null)} 
                  dismissible
                  className="mb-4 shadow-sm"
                >
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon 
                      icon={message.type === 'success' ? faCheck : faInfoCircle} 
                      className="me-2 fs-5"
                    />
                    <span>{message.text}</span>
                  </div>
                </Alert>
              )}
              
              {/* Onglets */}
              {step === 'initial' && (
                <Tabs
                  activeKey={activeTab}
                  onSelect={handleTabChange}
                  className="mb-4 nav-tabs-custom"
                >
                  <Tab 
                    eventKey="rdv" 
                    title={
                      <span className="tab-title">
                        <FontAwesomeIcon icon={faCalendarPlus} className="me-2" />
                        Prendre un rendez-vous
                      </span>
                    }
                  >
                    <div className="tab-content-wrapper p-3 p-lg-4 rounded-bottom shadow-sm">
                      <p className="lead mb-4">
                        Pour prendre un rendez-vous, veuillez saisir votre numéro de téléphone.
                        Nous vérifierons si vous êtes déjà inscrit dans notre système.
                      </p>
                      
                      <Form onSubmit={checkPatientExists} className="phone-form">
                        <Form.Group className="mb-4">
                          <Form.Label className="fw-bold">
                            <FontAwesomeIcon icon={faPhone} className="me-2 text-primary" />
                            Numéro de téléphone
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            name="telephone"
                            value={phoneForm.telephone}
                            onChange={handlePhoneChange}
                            placeholder="Ex: 77 123 45 67"
                            required
                            className="form-control-lg"
                          />
                          <Form.Text className="text-muted">
                            Entrez votre numéro de téléphone au format sénégalais
                          </Form.Text>
                        </Form.Group>
                        
                        <div className="d-grid">
                          <Button
                            variant="primary"
                            type="submit"
                            size="lg"
                            disabled={loading || !phoneForm.telephone}
                            className="btn-animated"
                          >
                            {loading ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Vérification...
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faSearch} className="me-2" />
                                Vérifier et continuer
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </Tab>
                  
                  <Tab 
                    eventKey="verification" 
                    title={
                      <span className="tab-title">
                        <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                        Vérifier un rendez-vous
                      </span>
                    }
                  >
                    <div className="tab-content-wrapper p-3 p-lg-4 rounded-bottom shadow-sm">
                      <p className="lead mb-4">
                        Pour vérifier l'état de votre rendez-vous, veuillez saisir le code de vérification
                        qui vous a été envoyé lors de la confirmation de votre rendez-vous.
                      </p>
                      
                      <Form onSubmit={verifyAppointment} className="verification-form">
                        <Form.Group className="mb-4">
                          <Form.Label className="fw-bold">
                            <FontAwesomeIcon icon={faCalendarCheck} className="me-2 text-primary" />
                            Code de vérification
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="code"
                            value={verificationForm.code}
                            onChange={handleVerificationChange}
                            placeholder="Ex: 123456"
                            required
                            className="form-control-lg text-center"
                            maxLength={6}
                          />
                          <Form.Text className="text-muted">
                            Le code à 6 chiffres que vous avez reçu par WhatsApp
                          </Form.Text>
                        </Form.Group>
                        
                        <div className="d-grid">
                          <Button
                            variant="primary"
                            type="submit"
                            size="lg"
                            disabled={loading || !verificationForm.code}
                            className="btn-animated"
                          >
                            {loading ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Vérification...
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faSearch} className="me-2" />
                                Vérifier le rendez-vous
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </Tab>
                </Tabs>
              )}
              
              {/* Formulaire d'inscription */}
              {step === 'register' && (
                <div className="register-form-container">
                  <div className="d-flex align-items-center mb-4">
                    <Button
                      variant="outline-secondary"
                      className="btn-circle me-3"
                      onClick={goBack}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>
                    <h4 className="mb-0 fw-bold">Inscription</h4>
                  </div>
                  
                  <div className="alert alert-info mb-4 shadow-sm">
                    <div className="d-flex">
                      <div className="me-3">
                        <FontAwesomeIcon icon={faInfoCircle} size="lg" />
                      </div>
                      <div>
                        <h5 className="alert-heading">Nouveau patient</h5>
                        <p className="mb-0">
                          Vous n'êtes pas encore inscrit. Veuillez compléter ce formulaire pour créer votre compte patient.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <Form onSubmit={registerPatient} className="registration-form">
                    <Card className="mb-4 shadow-sm">
                      <Card.Header className="bg-light">
                        <h5 className="mb-0">
                          <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                          Informations personnelles
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label className="fw-bold">Prénom et Nom du patient<span className="text-muted">(Optionnel)</span></Form.Label>
                              <Form.Control
                                type="text"
                                name="nom"
                                value={patientForm.nom}
                                onChange={handlePatientChange}
                                className="form-control-lg"
                              />
                            </Form.Group>
                          </Col>
                       
                        </Row>
                        
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label className="fw-bold">Date de naissance <span className="text-muted">(Optionnel)</span></Form.Label>
                              <Form.Control
                                type="date"
                                name="dateNaissance"
                                value={patientForm.dateNaissance}
                                onChange={handlePatientChange}
                                className="form-control-lg"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label className="fw-bold">Sexe <span className="text-muted">(Optionnel)</span></Form.Label>
                              <div className="d-flex mt-2">
                                <Form.Check
                                  type="radio"
                                  id="sexe-homme"
                                  name="sexe"
                                  value="homme"
                                  label={
                                    <span>
                                      <FontAwesomeIcon icon={faMars} className="me-2 text-primary" />
                                      Homme
                                    </span>
                                  }
                                  checked={patientForm.sexe === 'homme'}
                                  onChange={handlePatientChange}
                                  className="me-4 custom-radio"
                                />
                                <Form.Check
                                  type="radio"
                                  id="sexe-femme"
                                  name="sexe"
                                  value="femme"
                                  label={
                                    <span>
                                      <FontAwesomeIcon icon={faVenus} className="me-2 text-danger" />
                                      Femme
                                    </span>
                                  }
                                  checked={patientForm.sexe === 'femme'}
                                  onChange={handlePatientChange}
                                  className="custom-radio"
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                    
                    <Card className="mb-4 shadow-sm">
                      <Card.Header className="bg-light">
                        <h5 className="mb-0">
                          <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
                          Coordonnées et accès
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">Email <span className="text-muted">(Optionnel)</span></Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={patientForm.email}
                            onChange={handlePatientChange}
                            className="form-control-lg"
                          />
                        </Form.Group>
                        
                        <Form.Group className="mb-4">
                          <Form.Label className="fw-bold">Téléphone <span className="text-danger">(Obligatoire)</span></Form.Label>
                          <Form.Control
                            type="tel"
                            name="telephone"
                            value={patientForm.telephone}
                            onChange={handlePatientChange}
                            required
                            className="form-control-lg"
                            placeholder="Ex: 77 123 45 67"
                          />
                          <Form.Text className="text-muted">
                            Ce numéro sera utilisé pour vous envoyer des notifications par WhatsApp
                          </Form.Text>
                        </Form.Group>
                        
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label className="fw-bold">Mot de passe <span className="text-danger">(Obligatoire)</span></Form.Label>
                              <Form.Control
                                type="password"
                                name="password"
                                value={patientForm.password}
                                onChange={handlePatientChange}
                                required
                                className="form-control-lg"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6} className="mb-3">
                            <Form.Group>
                              <Form.Label className="fw-bold">Confirmer le mot de passe <span className="text-danger">(Obligatoire)</span></Form.Label>
                              <Form.Control
                                type="password"
                                name="confirmPassword"
                                value={patientForm.confirmPassword}
                                onChange={handlePatientChange}
                                required
                                className="form-control-lg"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                    
                    <div className="d-grid mt-4">
                      <Button
                        variant="primary"
                        type="submit"
                        size="lg"
                        disabled={loading}
                        className="btn-animated"
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Création en cours...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                            Créer mon compte
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </div>
              )}
              
              {/* Formulaire de rendez-vous */}
              {step === 'appointment' && (
                <div className="appointment-form-container">
                  <div className="d-flex align-items-center mb-4">
                    <Button
                      variant="outline-secondary"
                      className="btn-circle me-3"
                      onClick={goBack}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>
                    <h4 className="mb-0 fw-bold">Prendre un rendez-vous</h4>
                  </div>
                  
                  {patientFound && (
                    <div className="alert alert-success mb-4 shadow-sm">
                      <div className="d-flex">
                        <div className="me-3">
                          <FontAwesomeIcon icon={faCheck} size="lg" />
                        </div>
                        <div>
                          <h5 className="alert-heading">Bonjour {patientFound.nom} !</h5>
                          <p className="mb-0">
                            Vous pouvez maintenant choisir la date et l'heure de votre rendez-vous.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <Form onSubmit={bookAppointment} className="rdv-form">
                    <Card className="mb-4 shadow-sm">
                      <Card.Header className="bg-light">
                        <h5 className="mb-0">
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary" />
                          Date et heure du rendez-vous
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={6} className="mb-4">
                            <Form.Group>
                              <Form.Label className="fw-bold">
                                <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary" />
                                Date <span className="text-danger">(Obligatoire)</span>
                              </Form.Label>
                              <Form.Control
                                type="date"
                                name="date"
                                value={rdvForm.date}
                                onChange={handleRdvChange}
                                min={new Date().toISOString().split('T')[0]}
                                required
                                className="form-control-lg"
                              />
                              {rdvForm.date && (
                                <div className="mt-2 text-primary">
                                  <Badge bg="light" text="dark" className="date-badge">
                                    {getWeekday(rdvForm.date)}
                                  </Badge>
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6} className="mb-4">
                            <Form.Group>
                              <Form.Label className="fw-bold">
                                <FontAwesomeIcon icon={faClock} className="me-2 text-primary" />
                                Heure <span className="text-danger">(Obligatoire)</span>
                              </Form.Label>
                              <Form.Select
                                name="heure"
                                value={rdvForm.heure}
                                onChange={handleRdvChange}
                                required
                                disabled={availableSlots.length === 0}
                                className="form-control-lg"
                              >
                                {availableSlots.length > 0 ? (
                                  availableSlots.map((slot) => (
                                    <option key={slot} value={slot}>
                                      {slot}
                                    </option>
                                  ))
                                ) : (
                                  <option value="">Aucun créneau disponible</option>
                                )}
                              </Form.Select>
                              {availableSlots.length === 0 && (
                                <div className="mt-2">
                                  <Badge bg="danger" className="no-slots-badge">
                                    Aucun créneau disponible pour cette date
                                  </Badge>
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md={6} className="mb-4">
                            <Form.Group>
                              <Form.Label className="fw-bold">Type de consultation <span className="text-muted">(Optionnel)</span></Form.Label>
                              <Form.Select
                                name="typeConsultation"
                                value={rdvForm.typeConsultation}
                                onChange={handleRdvChange}
                                className="form-control-lg"
                              >
                                <option value="presentiel">En présentiel (au cabinet)</option>
                                <option value="domicile">À domicile</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">Motif de consultation <span className="text-muted">(Optionnel)</span></Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name="motif"
                            value={rdvForm.motif}
                            onChange={handleRdvChange}
                            placeholder="Décrivez brièvement le motif de votre consultation"
                            className="form-control-lg"
                          />
                        </Form.Group>
                      </Card.Body>
                    </Card>
                    
                    <div className="d-grid mt-4">
                      <Button
                        variant="success"
                        type="submit"
                        size="lg"
                        disabled={loading || availableSlots.length === 0}
                        className="btn-animated"
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Confirmation en cours...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                            Confirmer le rendez-vous
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </div>
              )}
              
              {/* Formulaire de modification de rendez-vous */}
              {step === 'edit' && (
                <div className="edit-form-container">
                  <div className="d-flex align-items-center mb-4">
                    <Button
                      variant="outline-secondary"
                      className="btn-circle me-3"
                      onClick={goBack}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>
                    <h4 className="mb-0 fw-bold">Modifier le rendez-vous</h4>
                  </div>
                  
                  <div className="alert alert-info mb-4 shadow-sm">
                    <div className="d-flex">
                      <div className="me-3">
                        <FontAwesomeIcon icon={faInfoCircle} size="lg" />
                      </div>
                      <div>
                        <h5 className="alert-heading">Modification de rendez-vous</h5>
                        <p className="mb-0">
                          Vous modifiez le rendez-vous n°{rdvFound.index} du {new Date(rdvFound.date).toLocaleDateString("fr-FR")} à {rdvFound.heure}.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <Form onSubmit={updateAppointment} className="rdv-form">
                    <Card className="mb-4 shadow-sm">
                      <Card.Header className="bg-light">
                        <h5 className="mb-0">
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary" />
                          Nouvelles date et heure
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={6} className="mb-4">
                            <Form.Group>
                              <Form.Label className="fw-bold">
                                <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary" />
                                Date <span className="text-danger">(Obligatoire)</span>
                              </Form.Label>
                              <Form.Control
                                type="date"
                                name="date"
                                value={rdvForm.date}
                                onChange={handleRdvChange}
                                min={new Date().toISOString().split('T')[0]}
                                required
                                className="form-control-lg"
                              />
                              {rdvForm.date && (
                                <div className="mt-2 text-primary">
                                  <Badge bg="light" text="dark" className="date-badge">
                                    {getWeekday(rdvForm.date)}
                                  </Badge>
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6} className="mb-4">
                            <Form.Group>
                              <Form.Label className="fw-bold">
                                <FontAwesomeIcon icon={faClock} className="me-2 text-primary" />
                                Heure <span className="text-danger">(Obligatoire)</span>
                              </Form.Label>
                              <Form.Select
                                name="heure"
                                value={rdvForm.heure}
                                onChange={handleRdvChange}
                                required
                                disabled={availableSlots.length === 0}
                                className="form-control-lg"
                              >
                                {availableSlots.length > 0 ? (
                                  availableSlots.map((slot) => (
                                    <option key={slot} value={slot}>
                                      {slot}
                                    </option>
                                  ))
                                ) : (
                                  <option value="">Aucun créneau disponible</option>
                                )}
                              </Form.Select>
                              {availableSlots.length === 0 && (
                                <div className="mt-2">
                                  <Badge bg="danger" className="no-slots-badge">
                                    Aucun créneau disponible pour cette date
                                  </Badge>
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md={6} className="mb-4">
                            <Form.Group>
                              <Form.Label className="fw-bold">Type de consultation <span className="text-muted">(Optionnel)</span></Form.Label>
                              <Form.Select
                                name="typeConsultation"
                                value={rdvForm.typeConsultation}
                                onChange={handleRdvChange}
                                className="form-control-lg"
                              >
                                <option value="presentiel">En présentiel (au cabinet)</option>
                                <option value="domicile">À domicile</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">Motif de consultation <span className="text-muted">(Optionnel)</span></Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name="motif"
                            value={rdvForm.motif}
                            onChange={handleRdvChange}
                            placeholder="Décrivez brièvement le motif de votre consultation"
                            className="form-control-lg"
                          />
                        </Form.Group>
                      </Card.Body>
                    </Card>
                    
                    <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mt-4">
                      <Button
                        variant="danger"
                        type="button"
                        onClick={() => setShowCancelModal(true)}
                        disabled={loading}
                        className="btn-animated"
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        Annuler le rendez-vous
                      </Button>
                      
                      <Button
                        variant="success"
                        type="submit"
                        disabled={loading || availableSlots.length === 0}
                        className="btn-animated"
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Enregistrement...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                            Enregistrer les modifications
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </div>
              )}
              
              {/* Confirmation */}
              {step === 'success' && rdvFound && (
                <div className="success-container text-center py-3">
                  <div className="mb-4">
                    <div className="success-icon mb-4">
                      <div className={`icon-circle ${rdvFound.statut === 'annulé' ? 'bg-warning' : 'bg-success'}`}>
                        <FontAwesomeIcon 
                          icon={rdvFound.statut === 'annulé' ? faExclamationTriangle : faCheck} 
                          className="text-white"
                          size="3x"
                        />
                      </div>
                    </div>
                    <h3 className="mb-3 fw-bold">
                      {activeTab === 'rdv' && rdvFound.statut !== 'annulé' ? 'Rendez-vous confirmé!' : 'Détails du rendez-vous'}
                    </h3>
                  </div>
                  
                  <Card className={`border-0 shadow mb-4 rdv-details-card ${rdvFound.statut === 'annulé' ? 'cancelled' : ''}`}>
                    <Card.Header className={`text-white ${rdvFound.statut === 'annulé' ? 'bg-danger' : 'bg-success'}`}>
                      <h5 className="mb-0">
                        {rdvFound.statut === 'annulé' ? 'Rendez-vous annulé' : 'Rendez-vous confirmé'}
                      </h5>
                    </Card.Header>
                    <Card.Body className="text-start p-4">
                      <Row className="mb-3 align-items-center">
                        <Col xs={5} className="fw-bold text-secondary">N° d'ordre du renedz-vousez-vous:</Col>
                        <Col xs={7}>
                          <Badge bg="primary" className="fs-6 px-3 py-2">{rdvFound.index}</Badge>
                        </Col>
                      </Row>
                      
                      <Row className="mb-3">
                        <Col xs={5} className="fw-bold text-secondary">Médecin:</Col>
                        <Col xs={7} className="fs-6">{rdvFound.medecinNom}</Col>
                      </Row>
                      
                      <Row className="mb-3">
                        <Col xs={5} className="fw-bold text-secondary">Patient:</Col>
                        <Col xs={7} className="fs-6">{rdvFound.patientNom}</Col>
                      </Row>
                      
                      <Row className="mb-3">
                        <Col xs={5} className="fw-bold text-secondary">Date:</Col>
                        <Col xs={7} className="fs-6">
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary" />
                            <span>
                              {new Date(rdvFound.date).toLocaleDateString("fr-FR", {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })}
                            </span>
                          </div>
                        </Col>
                      </Row>
                      
                      <Row className="mb-3">
                        <Col xs={5} className="fw-bold text-secondary">Heure:</Col>
                        <Col xs={7} className="fs-6">
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faClock} className="me-2 text-primary" />
                            <span>{rdvFound.heure}</span>
                          </div>
                        </Col>
                      </Row>
                      
                      <Row className="mb-3">
                        <Col xs={5} className="fw-bold text-secondary">Type:</Col>
                        <Col xs={7} className="fs-6">
                          {rdvFound.typeConsultation === 'teleconsultation' ? 'Téléconsultation' : 'Consultation en présentiel'}
                        </Col>
                      </Row>
                      
                      <Row className="mb-3">
                        <Col xs={5} className="fw-bold text-secondary">Lieu:</Col>
                        <Col xs={7} className="fs-6">
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faClinicMedical} className="me-2 text-primary" />
                            <span>{rdvFound.structureNom}</span>
                          </div>
                        </Col>
                      </Row>
                      
                      <Row className="mb-3">
                        <Col xs={5} className="fw-bold text-secondary">Statut:</Col>
                        <Col xs={7}>
                          <Badge 
                            bg={rdvFound.statut === 'annulé' ? 'danger' : 'success'} 
                            className="status-badge px-3 py-2"
                          >
                            {rdvFound.statut === 'annulé' ? 'Annulé' : 'Confirmé'}
                          </Badge>
                        </Col>
                      </Row>
                      
                      <Row className="mb-3">
                        <Col xs={5} className="fw-bold text-secondary">Code de vérification:</Col>
                        <Col xs={7}>
                          <div className="verification-code">
                            {rdvFound.verificationCode.split('').map((digit, index) => (
                              <span key={index} className="digit">{digit}</span>
                            ))}
                          </div>
                        </Col>
                      </Row>
                      
                      {rdvFound.motif && (
                        <Row className="mb-0">
                          <Col xs={5} className="fw-bold text-secondary">Motif:</Col>
                          <Col xs={7} className="fs-6">{rdvFound.motif}</Col>
                        </Row>
                      )}
                    </Card.Body>
                  </Card>
                  
                  <div className="alert alert-info shadow-sm">
                    <div className="d-flex">
                      <div className="me-3">
                        <FontAwesomeIcon icon={faInfoCircle} size="lg" />
                      </div>
                      <div className="text-start">
                        <h5 className="alert-heading">Notification WhatsApp</h5>
                        <p className="mb-0">
                          {rdvFound.statut === 'annulé' 
                            ? "Ce rendez-vous a été annulé. Une notification a été envoyée par WhatsApp."
                            : "Un message de confirmation avec tous les détails a été envoyé sur votre WhatsApp."
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-4">
                    <Button
                      variant="primary"
                      onClick={goBack}
                      size="lg"
                      className="btn-animated"
                    >
                      <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                      Retour à l'accueil
                    </Button>
                    
                    {rdvFound.statut !== 'annulé' && canModifyAppointment() && (
                      <Button
                        variant="secondary"
                        onClick={goToEdit}
                        size="lg"
                        className="btn-animated"
                      >
                        <FontAwesomeIcon icon={faEdit} className="me-2" />
                        Modifier ce rendez-vous
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Succès d'inscription uniquement */}
              {step === 'registrationSuccess' && (
                <div className="success-container text-center py-5">
                  <div className="mb-4">
                    <div className="success-icon mb-4">
                      <div className="icon-circle bg-success">
                        <FontAwesomeIcon icon={faCheck} className="text-white" size="3x" />
                      </div>
                    </div>
                    <h3 className="mb-3 fw-bold">Inscription réussie !</h3>
                    <p className="lead mb-4">Votre compte patient a été créé avec succès.</p>
                    <p className="mb-4">Vous avez reçu vos identifiants de connexion par WhatsApp.</p>
                  </div>

                  <Card className="border-0 shadow mb-4">
                    <Card.Body className="p-4">
                      <p className="fs-5 mb-2">Vous pouvez maintenant vous connecter à votre espace patient pour gérer vos rendez-vous et vos informations personnelles.</p>
                      <a href="https://www.samdigitale.com/Auth" className="btn btn-primary btn-lg mt-3" target="_blank" rel="noopener noreferrer">
                        Accéder à l'espace patient
                      </a>
                    </Card.Body>
                  </Card>

                  <Button variant="secondary" size="lg" onClick={goBack} className="btn-animated">
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Retour
                  </Button>
                </div>
              )}
            </Card.Body>
            <Card.Footer className="text-center py-3 bg-light">
              <small className="text-muted">© {new Date().getFullYear()} - SAMD | Sen Assistance Médicale Digitale</small>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
      
      {/* Modal de confirmation d'annulation */}
      <Modal 
        show={showCancelModal} 
        onHide={() => setShowCancelModal(false)}
        centered
        className="cancel-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="text-danger">Confirmer l'annulation</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <div className="text-center mb-4">
            <div className="warning-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning" size="4x" />
            </div>
          </div>
          <p className="text-center fs-5">
            Êtes-vous sûr de vouloir annuler votre rendez-vous n°{rdvFound?.index} du{' '}
            {rdvFound && new Date(rdvFound.date).toLocaleDateString("fr-FR")} à {rdvFound?.heure} ?
          </p>
          <p className="text-center text-danger fw-bold mb-0">
            Cette action est irréversible.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 justify-content-center flex-column flex-md-row gap-2">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowCancelModal(false)}
            className="px-4 py-2"
          >
            Non, conserver le rendez-vous
          </Button>
          <Button 
            variant="danger" 
            onClick={cancelAppointment}
            disabled={loading}
            className="px-4 py-2"
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Annulation...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Oui, annuler le rendez-vous
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <style jsx>{`
       /* PatientRegistration.css */

/* Styles généraux */
.patient-portal-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

/* Dégradé pour l'en-tête */
.bg-gradient-primary {
  background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
}

/* Styles des formulaires */
.form-control-lg {
  border-radius: 0.5rem;
  font-size: 1rem;
}

.form-control-lg:focus {
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Boutons */
.btn-animated {
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  border-radius: 0.5rem;
  font-weight: 500;
}

.btn-animated:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.btn-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Onglets personnalisés */
.nav-tabs-custom {
  border-bottom: 0;
}

.nav-tabs-custom .nav-link {
  border: none;
  padding: 1rem 1.5rem;
  color: #6c757d;
  font-weight: 500;
  border-radius: 0.5rem 0.5rem 0 0;
  transition: all 0.3s ease;
}

.nav-tabs-custom .nav-link:hover {
  color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.05);
}

.nav-tabs-custom .nav-link.active {
  color: #0d6efd;
  background-color: #fff;
  border-bottom: 3px solid #0d6efd;
}

.tab-content-wrapper {
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 0.5rem 0.5rem;
}

/* Styles des badges */
.date-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-weight: 500;
  text-transform: capitalize;
}

.no-slots-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-weight: 500;
}

.status-badge {
  font-weight: 500;
  border-radius: 1rem;
}

/* Styles pour la page de succès */
.success-icon {
  margin-bottom: 2rem;
}

.icon-circle {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.rdv-details-card {
  border-radius: 1rem;
  overflow: hidden;
}

.rdv-details-card.cancelled {
  opacity: 0.9;
}

/* Code de vérification stylisé */
.verification-code {
  display: flex;
  gap: 0.25rem;
}

.verification-code .digit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
  font-weight: bold;
  font-family: monospace;
  font-size: 1.2rem;
  color: #0d6efd;
}

/* Styles pour les radios personnalisés */
.custom-radio {
  font-size: 1.1rem;
}

/* Styles pour la modal d'annulation */
.cancel-modal .modal-content {
  border-radius: 1rem;
  border: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.warning-icon {
  margin: 1rem 0;
}

/* Responsive adjustments */
@media (max-width: 767.98px) {
  .tab-title {
    font-size: 0.9rem;
  }
  
  .icon-circle {
    width: 80px;
    height: 80px;
  }
  
  .verification-code .digit {
    width: 1.8rem;
    height: 1.8rem;
    font-size: 1rem;
  }
  
  .card-header h4, .card-header h5 {
    font-size: 1.1rem;
  }
}

@media (max-width: 575.98px) {
  .nav-tabs-custom .nav-link {
    padding: 0.75rem 1rem;
  }
  
  .verification-code {
    justify-content: center;
  }
  
  .btn-animated {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.register-form-container,
.appointment-form-container,
.edit-form-container,
.success-container {
  animation: fadeIn 0.5s ease-out;
}

      `}</style>
    </Container>
  );
};

export default PatientRegistration;


